import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PermissionWrapper from './Permissions/PermissionWrapper';
import { isCurrentUserDemo, isDemoFeatureAllowed } from '../utils/demoUserConfig';

const Container = styled.div`
  width: 100%;
  padding: 0 4px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100vh - 280px);
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 12px;
  margin-top: ${props => props.first ? '0' : '24px'};
`;

const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const AppCard = styled.div`
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 12px;
  padding: 14px 12px;
  cursor: pointer;
  min-height: 70px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  /* Gradient border - fades from left to right */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
    padding: 1px;
    background: linear-gradient(
      to right,
      rgba(255, 35, 0, 0.3) 0%,
      rgba(255, 35, 0, 0.5) 50%,
      rgba(255, 35, 0, 0.8) 85%,
      #FF2300 100%
    );
    -webkit-mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    transition: background 0.6s cubic-bezier(0.4, 0, 1, 1);
    z-index: 1;
  }
  
  /* Glow effect - starts on right spanning full height, expands to cover whole button on hover */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse 45% 100% at 95% 50%,
      rgba(255, 35, 0, 0.15) 0%,
      rgba(255, 35, 0, 0.08) 40%,
      rgba(255, 35, 0, 0.03) 70%,
      transparent 100%
    );
    pointer-events: none;
    border-radius: 12px;
    opacity: 1;
    transition: background 0.7s cubic-bezier(0.4, 0, 0.6, 1);
    z-index: 0;
  }
  
  &:hover {
    &::before {
      background: linear-gradient(
        to right,
        #FF2300 0%,
        #FF2300 100%
      );
    }
    
    &::after {
      background: radial-gradient(
        ellipse 180% 180% at 50% 50%,
        rgba(255, 35, 0, 0.14) 0%,
        rgba(255, 35, 0, 0.1) 25%,
        rgba(255, 35, 0, 0.06) 50%,
        rgba(255, 35, 0, 0.03) 70%,
        rgba(255, 35, 0, 0.01) 85%,
        transparent 100%
      );
    }
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const AppName = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: white;
  margin-bottom: 4px;
  position: relative;
  z-index: 2;
`;

const AppDescription = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.3;
  position: relative;
  z-index: 2;
`;

const AppsExpanded = () => {
  const navigate = useNavigate();
  const isDemo = isCurrentUserDemo();

  // Define app sections with colored categories (matching Landing page colors)
  const appSections = {
    intake: {
      title: 'Intake Apps',
      apps: [
        {
          id: 'connectors',
          name: 'Sync',
          description: 'Bandsintown · Spotify Apple',
          color: '#FF2300', // Red (matches Data/Connectors)
          onClick: () => navigate('/connectors')
        },
        {
          id: 'data-upload',
          name: 'Upload',
          description: 'Shows CSV · PRO CSV Label/Distro...',
          color: '#FF9500', // Orange (matches TourMngr)
          onClick: () => navigate('/connectors/data-upload')
        },
        {
          id: 'sheets',
          name: 'Sheets',
          description: 'Google Sheets',
          color: '#34C759', // Green (matches Analytics)
          onClick: () => navigate('/connectors/google-sheets')
        },
        {
          id: 'email',
          name: 'Email',
          description: 'Gmail · Apple Mail Outlook',
          color: '#17A2B8', // Teal (matches Catalog)
          onClick: () => navigate('/email')
        }
      ]
    },
    core: {
      title: 'Core Apps',
      apps: [
        {
          id: 'catalog',
          name: 'Releases',
          description: 'Release timeline',
          color: '#17A2B8', // Teal (matches Catalog)
          onClick: () => navigate('/catalog')
        },
        {
          id: 'works',
          name: 'Works',
          description: 'Splits + credits',
          color: '#FF9500', // Orange (matches TourMngr)
          onClick: () => navigate('/catalog/works')
        },
        {
          id: 'bookings',
          name: 'Shows',
          description: 'Live shows',
          color: '#FFFF00', // Yellow (matches Bookings)
          darkText: true,
          onClick: () => navigate('/bookings')
        },
        {
          id: 'tourmngr',
          name: 'TourMngr',
          description: 'Tour dates',
          color: '#FF9500', // Orange (matches TourMngr)
          onClick: () => navigate('/tour-manager/view-all')
        }
      ]
    },
    working: {
      title: 'Working Apps',
      apps: [
        {
          id: 'tasks',
          name: 'Tasks',
          description: 'Daily tasks',
          color: '#00CED1', // Cyan (matches Tasks)
          onClick: () => navigate('/tasks')
        },
        {
          id: 'workflows',
          name: 'Workflows',
          description: 'Automations',
          color: '#AAFF00', // Lime (matches Workflows)
          darkText: true,
          onClick: () => navigate('/flows')
        },
        {
          id: 'notes',
          name: 'Notes',
          description: 'Write + organize',
          color: '#F5F5F5', // Light gray (matches Notes)
          darkText: true,
          onClick: () => navigate('/notes')
        },
        {
          id: 'contacts',
          name: 'Contacts',
          description: 'People + teams',
          color: '#8B5CF6', // Purple (matches Teams)
          onClick: () => navigate('/contacts')
        }
      ]
    }
  };

  // Filter apps for demo users
  const filterAppsForDemo = (apps) => {
    if (!isDemo) return apps;
    const hiddenApps = ['workflows', 'bookings', 'catalog', 'tourmngr', 'contacts', 'works'];
    return apps.filter(app => !hiddenApps.includes(app.id));
  };

  return (
    <Container>
      {Object.entries(appSections).map(([sectionKey, section], sectionIndex) => {
        const filteredApps = filterAppsForDemo(section.apps);
        if (filteredApps.length === 0) return null;
        
        return (
          <div key={sectionKey}>
            <SectionTitle first={sectionIndex === 0}>{section.title}</SectionTitle>
            <AppsGrid>
              {filteredApps.map((app) => (
                <PermissionWrapper key={app.id} appName={app.id}>
                  <AppCard onClick={app.onClick}>
                    <AppName>{app.name}</AppName>
                    <AppDescription>{app.description}</AppDescription>
                  </AppCard>
                </PermissionWrapper>
              ))}
            </AppsGrid>
          </div>
        );
      })}
    </Container>
  );
};

export default AppsExpanded;

